<!--
  This page acts as an intermediary for Google Cloud Storage (GCS) signed download URLs
  sent via HubSpot transactional emails. 
  
  Problem:
  - HubSpot automatically appends UTM tracking parameters to all links in emails
  - These UTM params invalidate GCS signed URLs, causing downloads to fail
  - Additionally, HubSpot doesn't properly URL-encode the download URLs, causing the 
    browser to split GCS query parameters (like X-Goog-Credential) into separate params
  
  Solution:
  - Email links point to: /download?url=<GCS_SIGNED_URL>
  - This page extracts the base URL and all "widowed" GCS parameters from the query string
  - Uses a WHITELIST approach: only allows known GCS param patterns (X-Goog-*, response-*)
  - All other params (UTM, HubSpot tracking, etc.) are ignored
  - Validates domain is from allowed GCS domains (storage.googleapis.com, storage.cloud.google.com)
    to prevent open redirect security vulnerabilities
  - Reconstructs the complete GCS signed URL with only valid params
  - Redirects to the clean URL to trigger the file download
  
  Usage in HubSpot emails:
  https://my.clearbanc.com/download?url=<GCS_SIGNED_URL>
-->
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import ClearcoLogo from '@/assets/logos/clearco-logo.svg';
import DSButton from 'primevue/button';
import { ContactSupportMethods } from '@/data/contact-support';
import { RouteNames } from '@/data/routes';

const router = useRouter();
const TARGET_PARAM = 'url';

// Whitelist of allowed GCS signed URL parameter patterns
const GCS_PARAM_PATTERNS = ['X-Goog-', 'response-'];

// Allowed GCS domains to prevent open redirect vulnerabilities
const ALLOWED_GCS_DOMAINS = [
  'storage.googleapis.com',
  'storage.cloud.google.com',
];

const error = ref('');

const goHome = () => {
  router.push({ name: RouteNames.HOME.INDEX });
};

const processRedirect = () => {
  try {
    const currentParams = new URLSearchParams(window.location.search);

    const rawFileUrl = currentParams.get(TARGET_PARAM);

    if (!rawFileUrl) {
      throw new Error('No download link found.');
    }

    const finalUrl = new URL(rawFileUrl);

    if (!ALLOWED_GCS_DOMAINS.includes(finalUrl.hostname)) {
      throw new Error(
        'Invalid download link. Please contact support if you believe this is an error.',
      );
    }

    // Loop through ALL window parameters to find the "widowed" GCS params
    for (const [key, value] of currentParams.entries()) {
      if (key === TARGET_PARAM) continue;

      const isGcsParam = GCS_PARAM_PATTERNS.some((pattern) =>
        key.startsWith(pattern),
      );

      if (isGcsParam) {
        finalUrl.searchParams.append(key, value);
      }
    }

    // Use replace() so the user can't click "Back" and get stuck in a loop
    window.location.replace(finalUrl.toString());
  } catch (err) {
    error.value =
      err instanceof Error
        ? err.message
        : 'An error occurred while processing your download. Please contact support.';
  }
};

onMounted(() => {
  processRedirect();
});
</script>

<template>
  <div :class="$style.container">
    <div :class="$style.content">
      <ClearcoLogo :class="$style.logo" />
      <h1 :class="$style.title">Download Started</h1>
      <p :class="$style.message">
        Your download should start automatically. Once the download is complete
        you may safely close this tab.
      </p>
      <p v-if="error" :class="$style.error">
        {{ error }}
      </p>
      <div :class="$style.actions">
        <DSButton
          label="Return to Home"
          :class="$style.button"
          @click="goHome"
        />
      </div>
      <div :class="$style.support">
        Need help? Contact us at
        <a :href="ContactSupportMethods.email.href">{{
          ContactSupportMethods.email.display
        }}</a>
        or {{ ContactSupportMethods.phone.display }},
        {{ ContactSupportMethods.supportHours.display }}.
      </div>
    </div>
  </div>
</template>

<style lang="less" module>
.container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  padding: 20px;
}

.content {
  text-align: center;
  max-width: 500px;
  padding: 60px 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.logo {
  width: 160px;
  height: auto;
  margin-bottom: 26px;
}

.title {
  font-size: 24px;
  font-weight: 600;
  color: @color-grey-500;
  margin-bottom: 16px;
}

.message {
  font-size: 16px;
  color: @color-grey-400;
  margin: 0;
}

.error {
  margin-top: 20px;
  padding: 12px 16px;
  background-color: #fef2f2;
  color: #dc2626;
  border-radius: 6px;
  font-size: 14px;
}

.actions {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

.button {
  min-width: 160px;
}

.support {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid @color-grey-200;
  font-size: 14px;
  color: @color-grey-400;
  line-height: 1.5;

  a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
